import { ModalSlideshow } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ModalMFAContainer } from 'common/components/ModalMFA';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useCompleteOnboardingUserTaskMutation } from 'modules/onboarding/setup-hub/hooks/useCompleteOnboardingUserTaskMutation';
import { useGetOnboardingUserTasksQuery } from 'modules/onboarding/setup-hub/hooks/useGetOnboardingUserTasksQuery';
import { type UserTaskType } from 'modules/onboarding/setup-hub/types/userTask';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import type { Language } from 'src/core/config/i18n';
import { routeFor, routes } from 'src/core/constants/routes';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import maurice from './assets/maurice.svg';
import subscriptions from './assets/subscriptions.svg';
import { useCheckPhoneNumberAndRedirect } from '../../hooks/useCheckPhoneNumberAndRedirect';

const getSlideTwoAsset = (lang: Language) => {
  switch (lang) {
    case 'de':
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-2-de.mp4';
    case 'fr':
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-2-fr.mp4';
    default:
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-2-en.mp4';
  }
};

const getSlideThreeAsset = (lang: Language) => {
  switch (lang) {
    case 'de':
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-3-de.mp4';
    case 'fr':
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-3-fr.mp4';
    default:
      return 'https://spx-production.s3.eu-west-1.amazonaws.com/onboarding/slideshows/welcome-3-en.mp4';
  }
};

const WELCOME_TASK_TYPE: UserTaskType = 'welcome';

export const WelcomeModal = () => {
  const { activeLanguage, t } = useTranslation('global');
  const company = useCompany();
  const history = useHistory();

  const userTasksQueryState = useGetOnboardingUserTasksQuery();
  const userTasks = unwrapQuery(userTasksQueryState);
  const userTask = userTasks?.find((task) => task.type === WELCOME_TASK_TYPE);
  const isTaskOngoing = userTask?.status === 'ongoing';

  const [isOpen, setIsOpen] = useState(userTask?.status === 'ongoing');
  const [hasClosed, setHasClosed] = useState(false);

  const {
    isMfaModalOpen,
    trigger,
    handleMfaModalClose,
    handleFactorActivated,
  } = useCheckPhoneNumberAndRedirect();

  const openSubscription = () => {
    const path = routeFor(routes.REQUESTS.path, {
      company: company.id,
      type: 'all',
      id: 'new',
      newRequestType: 'virtual',
    });
    history.push(`${path}?card-type=subscription`, {
      productTourToDisplay: 'subscription',
    });
  };

  const [completeOnboardingUserTaskMutation] =
    useCompleteOnboardingUserTaskMutation();

  const slides = [
    {
      title: t('readyToSpend.slideshows.welcome.one.title'),
      content: <Trans i18nKey="readyToSpend.slideshows.welcome.one.content" />,
      illustration: <img src={maurice} alt="" />,
    },
    {
      title: t('readyToSpend.slideshows.welcome.two.title'),
      content: (
        <div>
          <p className="mb-24 body-l">
            {t('readyToSpend.slideshows.welcome.two.content')}
          </p>
          <ol className="flex list-inside list-decimal flex-col gap-16 font-bold text-content-primary">
            <li className="title-m">
              {t('readyToSpend.slideshows.welcome.two.septOne')}
            </li>
            <li className="title-m">
              {t('readyToSpend.slideshows.welcome.two.septTwo')}
            </li>
            <li className="title-m">
              {t('readyToSpend.slideshows.welcome.two.septThree')}
            </li>
          </ol>
        </div>
      ),
      illustration: (
        <video loop muted autoPlay>
          <source src={getSlideTwoAsset(activeLanguage)} type="video/mp4" />
        </video>
      ),
    },
    {
      title: t('readyToSpend.slideshows.welcome.three.title'),
      content: (
        <>
          <p className="body-l">
            {t('readyToSpend.slideshows.welcome.three.content')}
          </p>
          <div className="text-content-primary">
            <h3 className="mt-24 font-bold title-m">
              {t('readyToSpend.slideshows.welcome.three.singleUse.name')}
            </h3>
            <p className="mt-8">
              {t('readyToSpend.slideshows.welcome.three.singleUse.description')}
            </p>
            <h3 className="mt-24 font-bold title-m">
              {t('readyToSpend.slideshows.welcome.three.recurring.name')}
            </h3>
            <p className="mt-8">
              {t('readyToSpend.slideshows.welcome.three.recurring.description')}
            </p>
          </div>
        </>
      ),
      illustration: (
        <video loop muted autoPlay>
          <source src={getSlideThreeAsset(activeLanguage)} type="video/mp4" />
        </video>
      ),
    },
    {
      title: t('readyToSpend.slideshows.welcome.four.title'),
      content: (
        <>
          <p>{t('readyToSpend.slideshows.welcome.four.content')}</p>
          <p className="mt-16">
            {t('readyToSpend.slideshows.welcome.four.funFact')}
          </p>
        </>
      ),
      illustration: <img src={subscriptions} alt="" />,
    },
  ];

  useEffect(() => {
    if (!hasClosed) {
      setIsOpen(isTaskOngoing);
      if (isTaskOngoing) {
        track(AnalyticEventName.ONBOARDING_WALKTHROUGH_OPENED, {
          taskType: WELCOME_TASK_TYPE,
          totalSteps: slides.length,
          version: 0,
        });
      }
    }
  }, [isTaskOngoing]);

  const closeModal = async (isDone: boolean) => {
    setHasClosed(true);
    setIsOpen(false);
    // only open the subscription if "done" was clicked
    if (isDone) {
      trigger(openSubscription);
    }
    // mark the task as completed regardless of whether the user clicked "done" or "close"
    if (userTask && userTask.status !== 'completed') {
      // note: this endpoint can return a 500 while still marking the task as completed
      try {
        await completeOnboardingUserTaskMutation({
          endpointParams: { taskType: userTask.type },
        });
      } catch {
        // do nothing, we don't want to block the user from continuing
      }
    }
  };

  const onClose = async ({ index }: { index: number }) => {
    await closeModal(false);
    track(AnalyticEventName.ONBOARDING_WALKTHROUGH_CLOSED, {
      lastStepSeen: index,
      taskType: WELCOME_TASK_TYPE,
      totalSteps: slides.length,
      version: 0,
    });
  };

  const onDone = async () => {
    await closeModal(true);
    track(AnalyticEventName.ONBOARDING_WALKTHROUGH_DONE, {
      taskType: WELCOME_TASK_TYPE,
      totalSteps: slides.length,
      version: 0,
    });
  };

  return userTask ? (
    <>
      {userTask.status !== 'completed' && (
        <ModalSlideshow
          isOpen={isOpen}
          slides={slides}
          translations={{
            cancel: t('misc.cancel'),
            previous: t('misc.back'),
            next: t('misc.next'),
            done: t('misc.getStarted'),
          }}
          onDone={onDone}
          onClose={onClose}
          onCancel={() => onClose({ index: 0 })}
        />
      )}
      {/* We don't have the ModalMFAContainer under the "userTask.status !== 'completed'" condition because we mark the task as done as we open the MFA modal (task status will be 'completed', and we still want to show the ModalMFAContainer) */}
      <ModalMFAContainer
        isOpen={isMfaModalOpen}
        isReset
        onModalClose={handleMfaModalClose}
        onFactorActivated={handleFactorActivated}
        verifyPhoneStepTitle={t(
          'readyToSpend.slideshows.welcome.mfa.verifyPhoneStepTitle',
        )}
        tokenStepTitle={t('readyToSpend.slideshows.welcome.mfa.tokenStepTitle')}
        showAppCallout={false}
      />
    </>
  ) : null;
};
